import { useCoinMetricsApiKey, useGetExchangesReferenceQuery } from '@src/providers/CoinMetricsApiProvider';
import { useMemo } from 'react';
import { CM_MAX_PAGE_SIZE } from './tokens';

interface UseExchangeDisplayNameMapResult {
  /** CoinMetrics exchange id (lowercased) -> resolved display name. */
  displayNameByExchangeId: ReadonlyMap<string, string>;
  isLoading: boolean;
  isError: boolean;
}

const EMPTY_MAP: ReadonlyMap<string, string> = new Map();

/**
 * Fetches CoinMetrics exchange reference data (include=talos) and builds a lookup from CM
 * exchange id to display name: the Talos-native name when a mapping exists, else CoinMetrics'
 * own full_name. Entries with neither are omitted — callers fall back via
 * resolveExchangeDisplayName below.
 */
export function useExchangeDisplayNameMap(): UseExchangeDisplayNameMapResult {
  const apiKey = useCoinMetricsApiKey();

  const { data, isLoading, isUninitialized, isError } = useGetExchangesReferenceQuery(
    { include: 'talos', page_size: CM_MAX_PAGE_SIZE },
    { skip: !apiKey }
  );

  const displayNameByExchangeId = useMemo(() => {
    if (!data) {
      return EMPTY_MAP;
    }
    const map = new Map<string, string>();
    for (const entry of data.data) {
      const name = entry.talos?.at(0)?.display_name ?? entry.full_name;
      if (name) {
        map.set(entry.exchange.toLowerCase(), name);
      }
    }
    return map;
  }, [data]);

  // isUninitialized covers the skip: !apiKey state — key not yet synced into Redux.
  return { displayNameByExchangeId, isLoading: isLoading || isUninitialized, isError };
}

/**
 * Resolves a CoinMetrics exchange id to a display name: the Talos-native name when available
 * (from useExchangeDisplayNameMap), else falls back to title-casing the id (splitting on `-`/`_`)
 * so an unmapped or not-yet-loaded venue still reads reasonably (e.g. `some_new_dex` -> `Some New Dex`).
 * Exported for unit testing.
 */
export function resolveExchangeDisplayName(
  exchangeId: string,
  displayNameByExchangeId?: ReadonlyMap<string, string>
): string {
  const resolved = displayNameByExchangeId?.get(exchangeId.toLowerCase());
  if (resolved != null) {
    return resolved;
  }
  return exchangeId
    .split(/[-_]/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
